import _ from 'lodash'
import { FTProcess } from '@/models/FTProcess';
import { shapeArea, initProcess, ProcessConfig, ConversionResults, weightToLength, ParsedVariables } from './index'
import { ConversionDetails } from '@/models/ConversionDetails';

export function loadConfig({ processRow }: ParsedVariables): ProcessConfig {
  return {
    settings: {
      labelGap: {
        type: 'text',
        name: 'Gap Between Labels',
        unit: 'mm',
        default: 10,
        showIf: true
      }
    },
    inputs: {
      goods: [
        {
          goodsType: '',
          goodsFilter: {
            form: ['roll']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Input Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'single',
      arguments: [
        {
          name: 'Shape',
          type: 'shape',
          value: 'shape',
          optionsArray: [ 'Round', 'Oval', 'Rectangle', 'Rectangle+Oval', 'Other'],
          disabled: false
        },
        {
          name: 'Area',
          type: 'dimension',
          value: 'area',
          unit: 'sq. mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Other',
          disabled: false
        },
        {
          name: 'Diameter',
          type: 'dimension',
          value: 'diameter',
          unit: 'mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Round',
          disabled: false
        },
        {
          name: 'Width',
          type: 'dimension',
          value: 'width',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        },
        {
          name: 'Length',
          type: 'dimension',
          value: 'length',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'No of Pieces',
        value: 'numberOfPieces'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);
  const inputProcessRow = process.inputs[0]
  const outputFTProcessRow = process.outputs[0];
  conversionResult.outputs[0] = outputFTProcessRow;

  let linearLength= 0, totalInputArea = 0, piecesEstimate = 0;
  if(inputProcessRow.dimensions && inputProcessRow.quantity && inputProcessRow.goodsObject.dimensions) {
    linearLength = weightToLength(inputProcessRow.quantity, inputProcessRow.goodsObject.density, inputProcessRow.goodsObject.dimensions.width);
    totalInputArea = linearLength * (inputProcessRow.goodsObject.dimensions.width/1000);
  }
  const { diameter, width, length } = outputFTProcessRow.dimensions;
  const dieCutSheetWidthWithGapInM = ((diameter || width) + process.settings.labelGap) / 1000;
  const dieCutSheetLengthWithGapInM = ((diameter || length) + process.settings.labelGap) / 1000;
  piecesEstimate = totalInputArea/(dieCutSheetWidthWithGapInM * dieCutSheetLengthWithGapInM);
  outputFTProcessRow.goodsObject.copyFrom(inputProcessRow.goodsObject);
  outputFTProcessRow.goodsObject.form = 'die-cut-sheet';
  outputFTProcessRow.goodsObject.unitWeight = (outputFTProcessRow.goodsObject.density * shapeArea({
    shape: outputFTProcessRow.dimensions.shape,
    params: outputFTProcessRow.dimensions,
    outUnit: 'm'
  })) / 1000;
  outputFTProcessRow.quantity = outputFTProcessRow.goodsObject.unitWeight * piecesEstimate;
  const pcsPerKg = piecesEstimate / outputFTProcessRow.quantity;
  outputFTProcessRow.goodsObject.setPerKgRatio('plA', pcsPerKg);
  if (outputFTProcessRow.dimensions) {
    outputFTProcessRow.goodsObject.dimensions = outputFTProcessRow.dimensions;
  }

  conversionResult.details.waste = inputProcessRow.quantity - outputFTProcessRow.quantity;
  conversionResult.details.wastePercentage = (conversionResult.details.waste / inputProcessRow.quantity) * 100;
  conversionResult.details.netWeight = outputFTProcessRow.quantity;
  conversionResult.details.numberOfPieces = piecesEstimate;
  if (outputFTProcessRow.goodsObject.unitWeight) {
    conversionResult.details.kgPerHour = 60 * process.machineDetail.mechanicalSpeed * (inputProcessRow.dimensions.width / 1000) * inputProcessRow.goodsObject.density;
    conversionResult.details.plannedHours = inputProcessRow.quantity / conversionResult.details.kgPerHour;
  }
  conversionResult.details.pcsPerStroke = process.machineDetail.pcsPerStroke;

  return conversionResult;
}